"use client";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, FieldDescription, FieldError, FieldLabel, useFieldWrapperContext } from "./field-wrapper";
import type { FieldBindingProps, SelectOption } from "./types";

export interface SelectFieldProps extends FieldBindingProps<string> {
  label?: string;
  description?: string;
  placeholder?: string;
  options: SelectOption[];
  disabled?: boolean;
  isRequired?: boolean;
}

export function SelectField({
  value,
  onChange,
  onBlur,
  errors,
  isTouched,
  isRequired,
  label,
  description,
  placeholder = "Select...",
  options,
  disabled,
}: SelectFieldProps) {
  const items = Object.fromEntries(options.map((opt) => [opt.value, opt.label]));

  return (
    <Field errors={errors} isTouched={isTouched} isRequired={isRequired}>
      {label && <FieldLabel>{label}</FieldLabel>}
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldSelect
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        options={options}
        items={items}
        disabled={disabled}
        hasLabel={!!label}
      />
      <FieldError />
    </Field>
  );
}

function FieldSelect({
  value,
  onChange,
  onBlur,
  placeholder,
  options,
  items,
  disabled,
  hasLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  placeholder: string;
  options: SelectOption[];
  items: Record<string, React.ReactNode>;
  disabled?: boolean;
  hasLabel: boolean;
}) {
  const { id, hasError } = useFieldWrapperContext();
  return (
    <Select
      value={value || null}
      onValueChange={(v) => {
        if (v) onChange(v);
        onBlur();
      }}
      disabled={disabled}
      items={items}
    >
      <SelectTrigger
        id={id}
        className={`w-full ${hasLabel ? "mt-1" : ""}`}
        disabled={disabled}
        aria-invalid={hasError}
        aria-describedby={hasError ? `${id}-error` : undefined}
      >
        <SelectValue>{value ? undefined : <span className="text-muted-foreground">{placeholder}</span>}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
